import React from 'react';
import {sb} from '../lib/supabase.js';
import {queuedReportActor} from '../lib/queuedReportApi.js';
import {loadQueuedRpcRecovery} from '../lib/offlineRpcRecovery.js';
import type {QueuedRpcRecovery} from '../lib/offlineRpcRecovery.js';
import InlineNotice from '../shared/InlineNotice.jsx';
import Button from '../shared/Button.jsx';

// One operation owner for Open/Cancel/Submit. Forms provide their exact draft
// shape; no generic JSON editor and no guessed conversion of their answers.
export function useQueuedRpcRecovery<D>(
  formKind: string,
  callbacks: {
    capture: () => D;
    restore: (draft: D) => void;
    apply: (loaded: QueuedRpcRecovery) => void;
    error: (message: string) => void;
    opened: () => void;
    externallyBusy?: boolean;
    /**
     * A reason to refuse Open before anything is read, e.g. while unsaved work
     * that a recovery would replace is on screen. Reported through `error`;
     * the form's state is not touched.
     */
    refuseOpen?: () => string | null | undefined;
  },
) {
  const live = React.useRef(callbacks);
  live.current = callbacks;
  const generation = React.useRef(0);
  const operation = React.useRef<'opening' | 'submitting' | null>(null);
  const previous = React.useRef<{draft: D} | null>(null);
  const [loaded, setLoaded] = React.useState<QueuedRpcRecovery | null>(null);
  const [opening, setOpening] = React.useState(false);
  const [message, setMessage] = React.useState('');
  React.useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  async function open(csid: string) {
    if (operation.current || live.current.externallyBusy) return;
    const refusal = live.current.refuseOpen?.();
    if (refusal) {
      live.current.error(refusal);
      return;
    }
    const token = ++generation.current;
    operation.current = 'opening';
    setOpening(true);
    try {
      const next = await loadQueuedRpcRecovery(sb, csid, formKind);
      if (token !== generation.current) return;
      // Capture at publication, so an input changed during the read is retained.
      const draft = live.current.capture();
      live.current.apply(next); // validates before setting any form state
      if (!previous.current) previous.current = {draft};
      setLoaded(next);
      setMessage('');
      live.current.opened();
    } catch (error) {
      if (token === generation.current) live.current.error(error instanceof Error ? error.message : String(error));
    } finally {
      if (token === generation.current) {
        operation.current = null;
        setOpening(false);
      }
    }
  }
  function cancel() {
    if (operation.current || live.current.externallyBusy) return;
    generation.current++;
    if (previous.current) live.current.restore(previous.current.draft);
    previous.current = null;
    setLoaded(null);
    setMessage('');
  }
  async function beginSubmit() {
    if (operation.current || live.current.externallyBusy) return false;
    operation.current = 'submitting';
    try {
      if (loaded && (await queuedReportActor(sb)) !== loaded.actorProfileId) {
        throw new Error('Sign in with the account that opened this report. The draft was retained.');
      }
      return true;
    } catch (error) {
      operation.current = null;
      live.current.error(error instanceof Error ? error.message : String(error));
      return false;
    }
  }
  function finish(success = false, opts: {keepCurrent?: boolean} = {}) {
    operation.current = null;
    if (!success || !loaded) return false;
    const prior = previous.current;
    previous.current = null;
    setLoaded(null);
    if (opts.keepCurrent) {
      // The submitted replacement IS the current view (e.g. a weigh-in draft
      // converted in place); the pre-open draft is deliberately not restored.
      // The form refuses Open while unsaved work is on screen, so nothing
      // unsent is dropped here. The original leaves the device only once the
      // replacement verifies (acknowledgeQueuedReport), which may be later.
      setMessage('Replacement submitted. The earlier queued report is removed once the replacement is verified.');
      return true;
    }
    if (prior) live.current.restore(prior.draft);
    setMessage('Replacement retained. Your earlier draft is restored.');
    return true;
  }
  // retainedPhoto: the exact restored File, so an unchanged photo is reused
  // byte-for-byte instead of being re-encoded (see useOfflineRpcSubmit).
  const options = loaded
    ? {replacesCsid: loaded.csid, expectedActorProfileId: loaded.actorProfileId, retainedPhoto: loaded.photo}
    : {};
  const banner = loaded ? (
    <InlineNotice
      notice={{
        kind: 'info',
        message:
          'Review these queued answers. Submit creates a replacement; the original stays on this device until verified.',
      }}
      action={
        <Button variant="secondary" disabled={opening || !!callbacks.externallyBusy} onClick={cancel}>
          Cancel Recovery
        </Button>
      }
    />
  ) : message ? (
    <InlineNotice notice={{kind: 'info', message}} />
  ) : null;
  return {loaded, opening, open, cancel, beginSubmit, finish, options, banner};
}
